import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calculator, Loader2, AlertTriangle, FileSpreadsheet, Download, ChevronRight, Play, GitCompare, Users } from 'lucide-react';
import api from '@/lib/axios';
import { formatApiError } from '@/lib/errors';
import { matchesFreeText } from '@/components/search/SearchBuilder';
import { useTenant } from '@/contexts/TenantContext';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import BillingV1V2ComparePanel from './BillingV1V2ComparePanel';
import { formatDeNumber } from '@/lib/utils';
import { useClientListMode } from '@/hooks/useClientListMode';
import ListModePager from '@/components/ListModePager';

/**
 * Tenant-wide bulk invoice preview. Shows per-family breakdowns,
 * total monthly billing volume, and warning families with 0 € tariffs.
 */
export default function BillingPreviewPage() {
  const { activeTenant } = useTenant();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('');
  const [warningsOnly, setWarningsOnly] = useState(false);
  const [runDialogOpen, setRunDialogOpen] = useState(false);
  const [unbilledOpen, setUnbilledOpen] = useState(false);
  const [unbilledFilter, setUnbilledFilter] = useState('');
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState(null);
  const [billingMonth, setBillingMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [dryRun, setDryRun] = useState(true);

  const load = async () => {
    if (!activeTenant?.id) return;
    setLoading(true); setError(null);
    try {
      const res = await api.get('/billing/preview-month', {
        params: { tenant_id: activeTenant.id },
      });
      setData(res.data);
    } catch (e) {
      setError(formatApiError(e, 'Vorschau konnte nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeTenant?.id]);

  const families = (data?.families || []).filter((f) => {
    if (warningsOnly && !f.has_zero_tariff) return false;
    return matchesFreeText(f, filter);
  });

  const {
    listMode, setListMode, pageSize, setPageSize, listPage, setListPage,
    totalCount, pageCount, visibleItems: visibleFamilies,
  } = useClientListMode(families, {
    resetKey: `${filter}|${warningsOnly}|${data?.families?.length || 0}`,
  });

  const unbilledAll = data?.unbilled_students || [];
  const unbilledFiltered = unbilledAll.filter((s) => matchesFreeText(s, unbilledFilter));
  const {
    listMode: unbilledMode, setListMode: setUnbilledMode,
    pageSize: unbilledPageSize, setPageSize: setUnbilledPageSize,
    listPage: unbilledPage, setListPage: setUnbilledPage,
    totalCount: unbilledTotal, pageCount: unbilledPageCount,
    visibleItems: visibleUnbilled,
  } = useClientListMode(unbilledFiltered, {
    resetKey: `${unbilledFilter}|${unbilledAll.length}`,
  });

  const unbilledCount = data?.summary?.students_unbilled
    ?? Math.max(0, (data?.summary?.active_students_total || 0) - (data?.summary?.students_with_active_billing || 0));

  const exportCsv = () => {
    if (!data) return;
    const rows = [['Familie', 'Schüler', 'Belegungen', 'Warnung', 'Gesamtbetrag']];
    for (const f of data.families) {
      rows.push([
        f.family_label,
        f.student_names.join(' / '),
        f.line_items.length,
        f.has_zero_tariff ? 'JA' : '',
        formatDeNumber(f.grand_total),
      ]);
    }
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(';')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `rechnungs-vorschau_${data.tenant.slug}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success('CSV exportiert');
  };

  const runMonth = async () => {
    if (!activeTenant?.id) return;
    setRunning(true);
    setRunResult(null);
    try {
      const res = await api.post('/billing/run-month', {
        tenant_id: activeTenant.id,
        billing_month: `${billingMonth}-01`,
        dry_run: dryRun,
      });
      setRunResult(res.data);
      const { invoices_created, invoices_skipped, total_amount, dry_run } = res.data;
      toast.success(
        `${dry_run ? 'Dry-Run: ' : ''}${invoices_created} Rechnungen ${dry_run ? 'simuliert' : 'erstellt'}, ${invoices_skipped} übersprungen, ${formatDeNumber(total_amount)} €`
      );
    } catch (e) {
      toast.error(formatApiError(e, 'Abrechnungslauf fehlgeschlagen'));
    } finally {
      setRunning(false);
    }
  };

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="billing-preview-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Calculator className="w-6 h-6 text-emerald-600" /> Rechnungslauf-Vorschau
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Berechnung aller Familien-Monatsrechnungen für{' '}
              <span className="font-semibold">{activeTenant?.name}</span> nach
              Greven-Tarif (14-tg., GEMA, Familienrabatt).
            </p>
          </div>
          <div className="flex gap-2 items-center">
            <Button variant="outline" onClick={load} disabled={loading} data-testid="reload-preview">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Neu berechnen
            </Button>
            <Button variant="outline" onClick={exportCsv} disabled={!data} data-testid="export-csv">
              <Download className="w-4 h-4 mr-2" /> CSV
            </Button>
            <Button onClick={() => { setRunDialogOpen(true); setRunResult(null); }}
                    disabled={!data || data.summary.families_billed === 0}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="run-month-btn">
              <Play className="w-4 h-4 mr-2" /> Rechnungslauf starten
            </Button>
          </div>
        </div>

        {loading && !data && (
          <div className="flex items-center gap-2 p-8 text-slate-500">
            <Loader2 className="animate-spin" /> Berechnung läuft… (kann bei vielen Familien 5-15 Sek dauern)
          </div>
        )}
        {error && (
          <div className="p-4 bg-red-50 text-red-700 border border-red-200 rounded">{error}</div>
        )}

        <Tabs defaultValue="preview" className="w-full">
          <TabsList data-testid="billing-tabs-list">
            <TabsTrigger value="preview" data-testid="tab-preview">
              <Calculator className="w-4 h-4 mr-2" /> Vorschau
            </TabsTrigger>
            <TabsTrigger value="compare" data-testid="tab-compare">
              <GitCompare className="w-4 h-4 mr-2" /> v1 ↔ v2 Vergleich
            </TabsTrigger>
          </TabsList>

          <TabsContent value="preview" className="mt-6 space-y-6">
            {data && (
              <>
            {/* Summary cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              <SummaryCard label="Aktive Schüler" value={data.summary.active_students_total} />
              <SummaryCard label="Familien (mit Rechnung)" value={data.summary.families_billed} />
              <SummaryCard label="Familien mit Warnung" value={data.summary.families_with_warnings}
                accent={data.summary.families_with_warnings > 0 ? 'amber' : 'green'} />
              <SummaryCard label="Schüler abgerechnet" value={data.summary.students_with_active_billing} />
              <SummaryCard
                label="Ohne Abrechnung"
                value={unbilledCount}
                accent={unbilledCount > 0 ? 'amber' : 'green'}
                onClick={unbilledCount > 0 ? () => setUnbilledOpen(true) : undefined}
                hint={unbilledCount > 0 ? 'Klicken zum Anzeigen' : undefined}
                testId="summary-unbilled"
              />
              <SummaryCard
                label="Gesamtbetrag/Monat"
                value={`${formatDeNumber(data.summary.tenant_grand_total)} €`}
                accent="emerald"
                large
              />
            </div>

            {data.summary.families_with_warnings > 0 && (
              <div
                onClick={() => navigate('/tariffs-discounts?zeroOnly=1')}
                className="p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-900 flex items-center gap-2 cursor-pointer hover:bg-brand-red/10 transition-colors"
                data-testid="warnings-banner"
                role="button"
              >
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <span className="flex-1">
                  <strong>{data.summary.families_with_warnings} Familien</strong> haben mind. eine
                  Belegung mit Tarif 0,00 € — entweder den Tarif in der Gebührenverwaltung pflegen
                  oder die Belegung auf einen anderen Tarif umstellen.
                </span>
                <span className="text-xs font-semibold underline shrink-0">
                  Jetzt 0 €-Tarife pflegen →
                </span>
              </div>
            )}

            {/* Filter */}
            <div className="flex items-center gap-3">
              <Input
                placeholder="Suche nach Familienname oder Schüler…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="max-w-md"
                data-testid="family-filter"
              />
              <label className="text-sm flex items-center gap-2 cursor-pointer text-slate-700">
                <input
                  type="checkbox"
                  checked={warningsOnly}
                  onChange={(e) => setWarningsOnly(e.target.checked)}
                  data-testid="warnings-only"
                />
                Nur Familien mit Warnung
              </label>
              <span className="ml-auto text-xs text-slate-500">
                {families.length} / {data.families.length} Familien sichtbar
              </span>
            </div>

            {/* Family list */}
            <div className="border rounded-md overflow-hidden">
              <ListModePager
                listMode={listMode}
                setListMode={setListMode}
                pageSize={pageSize}
                setPageSize={setPageSize}
                listPage={listPage}
                setListPage={setListPage}
                totalCount={totalCount}
                pageCount={pageCount}
                visibleCount={visibleFamilies.length}
                label="Familien"
                testId="billing-preview-list-pager"
              />
              <div className="divide-y">
                {visibleFamilies.map((f) => (
                  <div
                    key={f.family_id || f.student_ids[0]}
                    className="px-4 py-3 hover:bg-slate-50 cursor-pointer flex items-center gap-3"
                    onClick={() => navigate(`/students/${f.student_ids[0]}?tab=billing`)}
                    data-testid={`family-row-${f.family_id || 'solo'}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-800 truncate">
                          {f.family_label}
                        </span>
                        {f.has_zero_tariff && (
                          <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold">
                            <AlertTriangle className="w-3 h-3" /> 0 €-Tarif
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 truncate">
                        {f.student_names.join(' • ')} ({f.line_items.length} Belegungen)
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-base font-bold text-emerald-700">
                        {formatDeNumber(f.grand_total)} €
                      </p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-300" />
                  </div>
                ))}
                {families.length === 0 && (
                  <div className="px-4 py-6 text-center text-slate-500 text-sm">
                    Keine Familien zum Filter gefunden.
                  </div>
                )}
              </div>
            </div>
          </>
        )}
          </TabsContent>

          <TabsContent value="compare" className="mt-6">
            <BillingV1V2ComparePanel tenantId={activeTenant?.id} defaultPeriod="2026-04-01" />
          </TabsContent>
        </Tabs>

        <Dialog open={runDialogOpen} onOpenChange={setRunDialogOpen}>
          <DialogContent data-testid="run-month-dialog">
            <DialogHeader>
              <DialogTitle>Rechnungslauf erstellen</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Abrechnungsmonat</label>
                <Input type="month" value={billingMonth} onChange={(e) => setBillingMonth(e.target.value)}
                       data-testid="run-month-input" />
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer p-2 rounded border border-amber-200 bg-brand-red/5">
                <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)}
                       data-testid="run-dryrun-toggle" />
                <span><strong>Dry-Run</strong> — keine Rechnungen anlegen, nur simulieren</span>
              </label>
              {!dryRun && (
                <div className="p-3 rounded-md bg-red-50 text-red-800 border border-red-200 text-sm">
                  ⚠️ <strong>Echter Lauf:</strong> Es werden persistente Invoice-Records angelegt. Bei Re-Run werden bereits abgerechnete Familien übersprungen (Idempotenz).
                </div>
              )}
              <div className="text-sm text-slate-600">
                Aktueller Mandant: <strong>{activeTenant?.name}</strong> · {data?.summary.families_billed || 0} Familien zu prüfen
              </div>
              {runResult && (
                <div className="border rounded-md p-3 bg-slate-50 text-sm space-y-1">
                  <div><strong>Modus:</strong> {runResult.dry_run ? 'Dry-Run' : 'Echter Lauf'}</div>
                  <div><strong>Erstellt:</strong> {runResult.invoices_created}</div>
                  <div><strong>Übersprungen:</strong> {runResult.invoices_skipped} {Object.keys(runResult.skipped_reasons || {}).length > 0 && `(${Object.entries(runResult.skipped_reasons).map(([k, v]) => `${v} ${k}`).join(', ')})`}</div>
                  <div><strong>Gesamtbetrag:</strong> {formatDeNumber(runResult.total_amount)} €</div>
                  {runResult.total_errors > 0 && <div className="text-red-700"><strong>Fehler:</strong> {runResult.total_errors}</div>}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRunDialogOpen(false)} disabled={running}>
                Schließen
              </Button>
              <Button onClick={runMonth} disabled={running} className={dryRun ? '' : 'bg-emerald-600 hover:bg-emerald-700'} data-testid="run-month-confirm-btn">
                {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
                {dryRun ? 'Simulation starten' : 'Lauf starten'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={unbilledOpen} onOpenChange={setUnbilledOpen}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="unbilled-students-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-amber-700" />
                Aktive Schüler ohne Abrechnung ({unbilledCount})
              </DialogTitle>
              <DialogDescription>
                Status „aktiv“, aber keine Position in diesem Rechnungslauf — meist ohne aktive Belegung
                oder ohne abrechenbaren Tarif.
              </DialogDescription>
            </DialogHeader>
            <Input
              placeholder="Suche Name oder Grund…"
              value={unbilledFilter}
              onChange={(e) => setUnbilledFilter(e.target.value)}
              className="mb-2"
              data-testid="unbilled-filter"
            />
            <div className="border rounded-md overflow-hidden flex-1 min-h-0 flex flex-col">
              <ListModePager
                listMode={unbilledMode}
                setListMode={setUnbilledMode}
                pageSize={unbilledPageSize}
                setPageSize={setUnbilledPageSize}
                listPage={unbilledPage}
                setListPage={setUnbilledPage}
                totalCount={unbilledTotal}
                pageCount={unbilledPageCount}
                visibleCount={visibleUnbilled.length}
                label="Schüler"
                testId="unbilled-list-pager"
              />
              <div className="divide-y overflow-y-auto max-h-[50vh]">
                {visibleUnbilled.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    className="w-full text-left px-4 py-2.5 hover:bg-slate-50 flex items-center gap-3"
                    onClick={() => {
                      setUnbilledOpen(false);
                      navigate(`/students/${s.id}?tab=billing`);
                    }}
                    data-testid={`unbilled-row-${s.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-900 truncate">
                        {s.last_name}, {s.first_name}
                      </div>
                      <div className="text-xs text-slate-500">{s.reason}</div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
                  </button>
                ))}
                {unbilledFiltered.length === 0 && (
                  <div className="px-4 py-8 text-center text-sm text-slate-500">
                    Keine Treffer.
                  </div>
                )}
              </div>
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                variant="outline"
                onClick={() => {
                  setUnbilledOpen(false);
                  navigate('/students?status=active&has_enrollment=false');
                }}
                data-testid="unbilled-open-students-list"
              >
                In Schülerliste öffnen
              </Button>
              <Button onClick={() => setUnbilledOpen(false)}>Schließen</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

const SummaryCard = ({ label, value, accent, large, onClick, hint, testId }) => {
  const accentClass = {
    emerald: 'border-emerald-200 bg-emerald-50',
    amber: 'border-amber-200 bg-brand-red/5',
    green: 'border-emerald-200 bg-emerald-50',
  }[accent] || 'border-slate-200 bg-white';
  const clickable = typeof onClick === 'function';
  const Comp = clickable ? 'button' : 'div';
  return (
    <Comp
      type={clickable ? 'button' : undefined}
      onClick={onClick}
      data-testid={testId}
      className={`border rounded-md p-3 text-left ${accentClass} ${clickable ? 'cursor-pointer hover:ring-2 hover:ring-amber-300/60 transition' : ''}`}
      title={hint}
    >
      <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">{label}</p>
      <p className={large ? 'text-2xl font-bold text-emerald-700 mt-1' : 'text-xl font-semibold text-slate-800 mt-1'}>
        {value}
      </p>
      {hint && clickable && (
        <p className="text-[10px] text-amber-800 mt-1">{hint}</p>
      )}
    </Comp>
  );
};
